import { COLORS_BY_ID, type ColorId } from '../data/colors'

interface Props {
  color: ColorId
  /** When true, renders the deck back instead of the front (for face-down picks). */
  faceDown?: boolean
  /** When set, the card is presented as the player's selection. */
  selected?: boolean
  /** Tile size — matches the game grid layouts. */
  size?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string
}

const SIZE_HEIGHT: Record<NonNullable<Props['size']>, string> = {
  sm: 'h-16',   // 64px tall × ~46px wide (portrait)
  md: 'h-24',   // 96  × ~70
  lg: 'h-32',   // 128 × ~93
  xl: 'h-44',   // 176 × ~128
}

/**
 * Color card art is intrinsically portrait, with the colour blob as
 * the primary read and a small label running along the edge. We
 * render it in its natural orientation — rotation is reserved for
 * the category cards (which have multi-column vertical text that
 * only reads cleanly when laid landscape; see CategoryCardImage).
 */
export function ColorCardImage({
  color, faceDown = false, selected = false, size = 'md', className = '',
}: Props) {
  const def = COLORS_BY_ID[color]
  if (!def) return null
  const src = faceDown ? def.cardBack : def.cardFront

  return (
    <div className={`flex flex-col items-center ${className}`}>
      <div
        className={`relative aspect-597/822 ${SIZE_HEIGHT[size]} rounded-xl overflow-hidden
                    border-2 transition-all
                    ${selected ? 'border-brand-text shadow-card-lg' : 'border-transparent'}`}
        style={{ backgroundColor: def.hex }}
      >
        <img
          src={src}
          alt={def.name}
          loading="lazy"
          draggable={false}
          className="w-full h-full object-cover select-none pointer-events-none"
        />
        {selected && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-white text-3xl drop-shadow-sm font-bold">✓</span>
          </div>
        )}
      </div>
      {/* Tiny colour-name label below — printed on the card art too,
          but rotated and easy to miss. This makes the colour
          unambiguous for colour-blind players. Hidden when face-down. */}
      {!faceDown && (
        <p className="text-[9px] font-medium text-brand-muted text-center mt-0.5
                      uppercase tracking-wide leading-tight">
          {def.name}
        </p>
      )}
    </div>
  )
}

/**
 * Category card art has multi-column vertical prompts (1, 2, 3, 4-5)
 * that only read cleanly as rows. We size the inner <img> to a
 * portrait box that perfectly tiles a landscape container post-
 * rotation, then `transform: rotate(-90deg)`. No asset re-encoding.
 */
interface CategoryCardImageProps {
  src: string
  alt: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string
}

const CATEGORY_HEIGHT: Record<NonNullable<CategoryCardImageProps['size']>, string> = {
  sm: 'h-14',
  md: 'h-20',
  lg: 'h-28',
  xl: 'h-40',
}

export function CategoryCardImage({
  src, alt, size = 'lg', className = '',
}: CategoryCardImageProps) {
  return (
    <div
      className={`relative aspect-822/597 ${CATEGORY_HEIGHT[size]} rounded-2xl overflow-hidden
                  shadow-card-lg ${className}`}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        draggable={false}
        className="absolute select-none pointer-events-none"
        style={{
          top: '50%', left: '50%',
          width: 'calc(100% * 597 / 822)',
          transform: 'translate(-50%, -50%) rotate(-90deg)',
          transformOrigin: 'center',
        }}
      />
    </div>
  )
}
